// File: client/src/components/analytics/chartUtils.js
import { startOfDay, startOfWeek, startOfMonth, format, parseISO } from 'date-fns';

export const chartColors = [
    '#10b981', // green
    '#3b82f6', // blue
    '#f59e0b', // amber
    '#ec4899', // pink
    '#8b5cf6', // purple
    '#06b6d4', // cyan
    '#ef4444', // red
    '#84cc16'  // lime
];

export const aggregateDataByGranularity = (data, granularity, metrics) => {
    // Group data by the appropriate time period.
    const groupedData = data.reduce((acc, item) => {
        let key;
        const date = parseISO(item.date);
        switch (granularity) {
            case 'week':
                key = format(startOfWeek(date), 'yyyy-MM-dd');
                break;
            case 'month':
                key = format(startOfMonth(date), 'yyyy-MM');
                break;
            case 'all':
                key = 'all';
                break;
            default: // day
                key = format(startOfDay(date), 'yyyy-MM-dd');
        }
        if (!acc[key]) {
            acc[key] = {
                date: key,
                count: 0,
                // Initialize every metric key to 0.
                ...metrics.reduce((obj, metric) => {
                    obj[metric.id] = 0;
                    return obj;
                }, {}),
            };
        }
        // Increment count regardless of metric.
        acc[key].count++;

        metrics.forEach((metric) => {
            if (item[metric.id] !== undefined) {
                acc[key][metric.id] += Number(item[metric.id]);
            }
        });

        return acc;
    }, {});

    // Convert groups to an array and compute final values.
    return Object.values(groupedData)
        .map((group) => {
            const result = { ...group };
            metrics.forEach((metric) => {
                if (group.count > 0) {
                    result[metric.id] = group[metric.id] / group.count;
                }
            });
            return result;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));
};

export const calculateAverages = (data, metrics) => {
    // Special case for volume (when no metrics are selected)
    if (metrics.length === 0 || metrics.some(m => m.id === 'volume')) {
        const result = {};

        // Handle volume/count average
        const uniqueDates = new Set(data.map(item =>
            typeof item.date === 'string' ? item.date.substring(0, 10) : item.date
        ));
        const timePeriodsCount = uniqueDates.size || 1; // Avoid division by zero

        // Calculate average as total count divided by number of time periods
        result.volume = data.length / timePeriodsCount;

        // Handle other metrics
        metrics.forEach(metric => {
            if (metric.id !== 'volume') {
                const values = data
                    .map(item => Number(item[metric.id]))
                    .filter(value => !isNaN(value));

                result[metric.id] = values.length > 0
                    ? values.reduce((sum, val) => sum + val, 0) / values.length
                    : 0;
            }
        });

        return result;
    }

    // Standard behavior for regular metrics
    return metrics.reduce((acc, metric) => {
        const values = data
            .map(item => Number(item[metric.id]))
            .filter(value => !isNaN(value));

        acc[metric.id] = values.length > 0
            ? values.reduce((sum, val) => sum + val, 0) / values.length
            : 0;

        return acc;
    }, {});
};

export const CHART_PRESETS = {
    agentPerformance: {
        metrics: [
            { id: 'profScore', label: 'Professionalism Score', showAverage: true },
            { id: 'protScore', label: 'Protocol Score', showAverage: true },
            { id: 'qualScore', label: 'Quality Score', showAverage: true }
        ],
        granularity: 'day'
    },
    callVolume: {
        metrics: [],
        granularity: 'day'
    }
};
